import { useState } from "react";
import dayjs from "dayjs";
import { RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/layout/Page";
import { OwnerBookingCard } from "@/components/owner/OwnerBookingCard";
import { TripCardSkeleton } from "@/components/TripCard";
import { useOwnerBookings, useOwnerClients, useOwnerSummary } from "@/hooks/queries";
import { initials, money } from "@/lib/format";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "bookings", label: "Bookings" },
  { id: "clients", label: "Clients" },
];
const TIME_FILTERS = [["upcoming", "Upcoming"], ["past", "Past"], ["all", "All"]];
const PAYMENT_FILTERS = [["all", "Any payment"], ["to_verify", "To verify"], ["unpaid", "Unpaid"], ["verified", "Verified"]];

const Chips = ({ options, value, onChange, prefix }) => (
  <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:px-0">
    {options.map(([id, label]) => (
      <button key={id} type="button" className="chip" data-active={value === id} onClick={() => onChange(id)} data-testid={`${prefix}-${id}`}>
        {label}
      </button>
    ))}
  </div>
);

function Stat({ label, value, sub, gold, testId }) {
  return (
    <div className="lux-card rise p-5" data-testid={testId}>
      <p className="eyebrow text-[10px]">{label}</p>
      <p className={`mt-2 font-display text-3xl ${gold ? "text-gold" : "text-ivory"}`} data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-faint">{sub}</p>}
    </div>
  );
}

function BookingList({ query, emptyText }) {
  const { data = [], isLoading, isError, refetch } = query;
  if (isLoading) return [0, 1, 2].map((i) => <TripCardSkeleton key={i} />);
  if (isError)
    return (
      <p className="lux-card p-5 text-sm text-mist" data-testid="owner-bookings-error">
        Couldn't load bookings. <button type="button" className="text-gold underline" onClick={() => refetch()}>Retry</button>
      </p>
    );
  if (!data.length) return <p className="lux-card p-6 text-sm text-mist" data-testid="owner-bookings-empty">{emptyText}</p>;
  return data.map((b, i) => <OwnerBookingCard key={b.id} booking={b} index={i} />);
}

function Overview() {
  const { data: s } = useOwnerSummary();
  const upcoming = useOwnerBookings("upcoming", "all");
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Clients" value={s?.clients ?? "—"} testId="owner-stat-clients" />
        <Stat label="Upcoming rides" value={s?.upcoming_rides ?? "—"} sub={s && `${s.total_bookings} bookings all-time`} testId="owner-stat-upcoming" />
        <Stat label="Collected" value={s ? money(s.collected) : "—"} sub={s && `${s.verified_count} verified payments`} gold testId="owner-stat-collected" />
        <Stat label="To verify" value={s ? money(s.to_verify) : "—"} sub={s && `${s.to_verify_count} PayPal / Cash App payments`} testId="owner-stat-to-verify" />
        <div className="col-span-2 lg:col-span-1">
          <Stat label="Outstanding" value={s ? money(s.outstanding) : "—"} sub={s && `${s.unpaid_count} unpaid rides`} testId="owner-stat-outstanding" />
        </div>
      </div>
      <section>
        <h2 className="mb-4 text-xl text-ivory">Next up</h2>
        <div className="space-y-3"><BookingList query={{ ...upcoming, data: upcoming.data?.slice(0, 5) }} emptyText="No upcoming rides booked." /></div>
      </section>
    </div>
  );
}

function Bookings() {
  const [filter, setFilter] = useState("upcoming");
  const [payment, setPayment] = useState("all");
  const query = useOwnerBookings(filter, payment);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Chips options={TIME_FILTERS} value={filter} onChange={setFilter} prefix="owner-filter" />
        <button type="button" onClick={() => query.refetch()} className="btn-ghost h-10 w-10 p-0" title="Refresh" data-testid="owner-bookings-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin text-gold" : ""}`} />
        </button>
      </div>
      <Chips options={PAYMENT_FILTERS} value={payment} onChange={setPayment} prefix="owner-payment-filter" />
      <div className="space-y-3 pt-2" data-testid="owner-bookings-list"><BookingList query={query} emptyText="No bookings match these filters." /></div>
    </div>
  );
}

function Clients() {
  const { data = [], isLoading } = useOwnerClients();
  if (isLoading) return [0, 1].map((i) => <TripCardSkeleton key={i} />);
  if (!data.length) return <p className="lux-card p-6 text-sm text-mist" data-testid="owner-clients-empty">No clients yet.</p>;
  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid="owner-clients-list">
      {data.map((c, i) => (
        <div key={c.id} className="lux-card rise flex items-center gap-4 p-4 sm:p-5" style={{ animationDelay: `${i * 50}ms` }} data-testid={`owner-client-card-${c.id}`}>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-ink font-display text-lg text-gold">{initials(c.name)}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-ivory">{c.name}</p>
            <p className="truncate text-xs text-faint">{c.email}</p>
            <p className="mt-1 text-[11px] text-mist">
              {c.membership_tier} · since {dayjs(c.created_at).format("MMM YYYY")}
              {c.last_ride && ` · last ride ${dayjs(c.last_ride).format("MMM D")}`}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="font-display text-lg text-gold">{money(c.paid_total)}</p>
            <p className="text-[11px] text-mist">{c.rides} rides · {c.upcoming} upcoming</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const VIEWS = { overview: Overview, bookings: Bookings, clients: Clients };

export default function Owner() {
  const [tab, setTab] = useState("overview");
  const View = VIEWS[tab];
  return (
    <div data-testid="owner-page">
      <PageHeader eyebrow="Owner" title="Operations" />
      <div className="rise stagger-1 mb-8 grid max-w-md grid-cols-3 gap-1 rounded-full border border-line bg-surface p-1" data-testid="owner-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`owner-tab-${t.id}`}
            className={`rounded-full px-3 py-2.5 text-sm transition-[background-color,color] duration-200 ${tab === t.id ? "bg-gold font-semibold text-ink" : "text-mist hover:text-ivory"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <View />
    </div>
  );
}
